<template>
  <client-only>
    <div ref="wrapperRef" class="component">
      <div class="tolkie-button-wrapper tolkie-ignore">
        <template v-for="tolkieButton in visibleButtons" :key="tolkieButton.type">
          <Button
            type="default"
            variant="secondary"
            outline
            :class="['tolkie-button', tolkieButton.type]"
            :label="tolkieButton.label"
            :icon="tolkieButton.icon"
            @click="forceRemoveActive"
            @keydown.enter.prevent
          />
        </template>
      </div>
      <Transition name="fade" mode="out-in" appear>
        <div
          v-if="isTranslationAlertVisible"
          class="tolkie-translation-alert tolkie-ignore notranslate"
          translate="no"
          data-nosnippet
        >
          <Icon name="eenvandaag-general:alert-circle" class="tolkie-translation-alert-icon" />
          <p>
            This article was written in Dutch and translated by AI. It may contain errors. Please
            <NuxtLink to="/chat">tell us in the chat</NuxtLink> if you notice any problems.
          </p>
        </div>
      </Transition>
    </div>
  </client-only>
</template>

<script setup lang="ts">
import { useIntersectionObserver } from '@vueuse/core';

// Reactive variable to control the visibility of the alert
const isTranslationAlertVisible = ref(false);
let htmlTagObserver = null;

const wrapperRef = ref<HTMLElement | null>(null);

const props = defineProps<{
  features?: string[];
}>();

const tolkieButtons = [
  {
    label: 'Lees voor',
    icon: 'eenvandaag-general:sound-on',
    type: 'tolkie-tts-button',
    tolkieType: 'tts',
  },
  {
    label: 'Simpele tekst',
    icon: 'eenvandaag-general:align-left',
    type: 'tolkie-summary-button',
    tolkieType: 'summary',
  },
  {
    label: 'Uitleg woorden',
    icon: 'eenvandaag-general:book',
    type: 'tolkie-dictionary-button',
    tolkieType: 'dictionary',
  },
  {
    label: 'Tekstgrootte',
    icon: 'eenvandaag-general:font',
    type: 'tolkie-fontsize-button',
    tolkieType: 'fontSize',
  },
  {
    label: 'Dyslexie',
    icon: 'eenvandaag-general:dyslexia',
    type: 'tolkie-dyslexia-font-button',
    tolkieType: 'dyslexiaFont',
  },
  {
    label: 'Vertalen',
    icon: 'eenvandaag-general:earth',
    type: 'tolkie-translate-button',
    tolkieType: 'translate',
  },
  { label: 'Meer', icon: 'eenvandaag-general:plus', type: 'tolkie-tool-button' },
];

const visibleButtons = computed(() => {
  if (!props.features) return tolkieButtons;
  return tolkieButtons.filter((b) => props.features!.includes(b.type));
});

const visibleTolkieTypes = computed(() =>
  visibleButtons.value.map((b) => b.tolkieType).filter(Boolean),
);

const disabledTolkieTypes = computed(() => {
  return tolkieButtons
    .map((b) => b.tolkieType)
    .filter((t) => t && !visibleTolkieTypes.value.includes(t));
});

const initTolkie = () => {
  const run = () => {
    window.tolkie?.init?.({
      disabled: disabledTolkieTypes.value,
    });
  };

  if (window.tolkie?.init) {
    run();
  } else {
    const start = Date.now();
    const interval = setInterval(() => {
      if (window.tolkie?.init) {
        clearInterval(interval);
        run();
      } else if (Date.now() - start > 5000) {
        clearInterval(interval);
      }
    }, 50);
  }
};

const didInitOnMount = ref(false);
const didInitOnIntersect = ref(false);

onMounted(() => {
  if (didInitOnMount.value) return;
  didInitOnMount.value = true;
  initTolkie();

  // Initial check on mount
  checkCurrentLanguage();

  // Setup the observer to watch for changes on the <html> tag
  htmlTagObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.attributeName === 'lang') {
        checkCurrentLanguage();
      }
    });
  });

  // Start observing attribute changes
  htmlTagObserver.observe(document.documentElement, {
    attributes: true,
  });
});

useIntersectionObserver(wrapperRef, (entries) => {
  const entry = entries[0];

  if (entry?.isIntersecting && !didInitOnIntersect.value) {
    didInitOnIntersect.value = true;
    initTolkie();
  }
});

const forceRemoveActive = () => {
  // 1. Haal het daadwerkelijke HTML element uit de ref
  const element = wrapperRef.value as HTMLElement | null;

  // 2. Check of het element bestaat. Zo niet, stop direct.
  // Dit lost de "Unresolved" of "null" errors op.
  if (!element) return;

  const removeClass = () => {
    const activeButtons = element?.querySelectorAll('.tolkie-button.active');

    if (!activeButtons) return;

    activeButtons.forEach((btn) => {
      btn.classList.remove('active');
      (btn as HTMLElement).blur(); // Haal focus weg
    });
  };

  // Voer de logica uit (direct, en met vertraging voor de zekerheid)
  removeClass();
  setTimeout(removeClass, 50);
  setTimeout(removeClass, 150);
};

onUnmounted(() => {
  // Clean up the observer to prevent memory leaks
  if (htmlTagObserver) {
    htmlTagObserver.disconnect();
  }
});

const checkCurrentLanguage = () => {
  const currentLang = document.documentElement.lang.toLowerCase();

  // Logic: Show alert for any language that doesn't start with 'nl'
  isTranslationAlertVisible.value = !!(currentLang && !currentLang.startsWith('nl'));
};
</script>

<style scoped lang="postcss">
.component {
  display: inline;
}

.tolkie-button-wrapper {
  display: inline;
}
.tolkie-button {
  flex-shrink: 0;
  margin-right: var(--unit-8);
  margin-bottom: var(--unit-8);
}

.tolkie-translation-alert-icon {
  height: 15px;
  width: 15px;
  margin: var(--unit-2) 0 0 0;
  flex-shrink: 0;

  @media (--md-viewport) {
    height: 20px;
    width: 20px;
  }
}

.tolkie-translation-alert {
  display: flex;
  padding: 12px 16px;
  align-items: flex-start;
  gap: 6px;
  align-self: stretch;
  background: hsl(var(--color-neutral-white));
  border: 1px solid hsl(var(--color-primary-100));
  color: hsl(var(--color-primary-600));
  margin: 10px 0 0;
  border-radius: 8px;
  font-style: italic;
  font-size: 16px;

  &:deep(a) {
    text-decoration: underline;
    font-weight: 500;
    color: hsl(var(--color-secondary-500));
  }
}
</style>
